// HelpCenter.tsx
import React, { useEffect, useMemo, useState } from "react";
import "./HelpCenter.css";

import { faqSections, trainingSections, trainingTiles, type FAQSection, type TrainingTile } from "./HelpCenterData";
import {
  buildAutoplayUrl,
  countFaqs,
  fetchVimeoThumbnail,
  filterSections,
  toDownloadUrl,
} from "./HelpCenterUtils";
import PromptDictionary, { promptSections } from "./PromptDictionary";

const HelpCenter: React.FC = () => {
  const initialTab = (() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab === "training" || tab === "prompts") return tab;
    return "faqs";
  })();
  const [activeTab, setActiveTabState] = useState<"faqs" | "training" | "prompts">(initialTab);

  const setActiveTab = (tab: "faqs" | "training" | "prompts") => {
    setActiveTabState(tab);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.replaceState(null, "", url.toString());
  };
  const [query, setQuery] = useState("");
  const [openKeys, setOpenKeys] = useState<Record<string, boolean>>({});
  const [activeSectionId, setActiveSectionId] = useState<string>("getting-started");

  // Thumbnails fetched from Vimeo oEmbed
  const [thumbMap, setThumbMap] = useState<Record<string, string>>({});

  // Training modal state
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState<string>("");

  const openTrainingVideo = (url: string) => {
    setActiveVideoUrl(buildAutoplayUrl(url));
    setVideoModalOpen(true);
  };

  const closeTrainingVideo = () => {
    setVideoModalOpen(false);
    setTimeout(() => setActiveVideoUrl(""), 150);
  };

  // // Fetch thumbnails once
  // useEffect(() => {
  //   let cancelled = false;

  //   (async () => {
  //     for (const tile of trainingTiles) {
  //       // If tile has explicit thumbnailUrl, skip fetch
  //       if (tile.thumbnailUrl) continue;

  //       // If already fetched, skip
  //       if (thumbMap[tile.videoUrl]) continue;

  //       try {
  //         const thumb = await fetchVimeoThumbnail(tile.videoUrl);
  //         if (!cancelled && thumb) {
  //           setThumbMap((prev) => ({ ...prev, [tile.videoUrl]: thumb }));
  //         }
  //       } catch (e) {
  //         // keep silent-ish; we already have a placeholder UI
  //         // eslint-disable-next-line no-console
  //         console.error("Failed to load thumbnail", e);
  //       }
  //     }
  //   })();

  //   return () => {
  //     cancelled = true;
  //   };
  //   // We intentionally only depend on trainingTiles (static) and thumbMap (to avoid refetch)
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [thumbMap]);
  useEffect(() => {
  let cancelled = false;

  (async () => {
    for (const tile of trainingTiles) {
      // skip if you manually provided one
      if (tile.thumbnailUrl) continue;

      // skip if already fetched
      if (thumbMap[tile.videoUrl]) continue;

      const thumb = await fetchVimeoThumbnail(tile.videoUrl);
      if (!cancelled && thumb) {
        setThumbMap((prev) => ({
          ...prev,
          [tile.videoUrl]: thumb,
        }));
      }
    }
  })();

  return () => {
    cancelled = true;
  };

// IMPORTANT:
// - trainingTiles is a constant import, so it's stable
// - we include thumbMap so it doesn't refetch the same URLs forever
}, [thumbMap]);


  const sections: FAQSection[] = useMemo(() => faqSections, []);
  const filteredSections = useMemo(() => filterSections(sections, query), [sections, query]);

  // Keep left-nav selection sensible when searching
  useEffect(() => {
    const first = filteredSections[0]?.id || "getting-started";
    setActiveSectionId(first);
  }, [filteredSections]);

  const toggle = (key: string) => {
    setOpenKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const scrollToSection = (id: string) => {
    setActiveSectionId(id);
    const el = document.getElementById(`hc-sec-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const totalFaqs = useMemo(() => countFaqs(sections), [sections]);

  return (
    <div className="hc-page">
      <div className="hc-hero">
        <div className="hc-hero-inner">
          <div className="hc-badge">
            <span className="hc-badge-dot" aria-hidden="true" />
            Internal Tool
          </div>

          <div className="hc-hero-title">JobsOhio Enterprise Chat - Help Center</div>
          <div className="hc-hero-subtitle">
            Find answers fast, review policies, and access quick training materials.
          </div>

          <div className="hc-tabs" role="tablist" aria-label="Help Center sections">
            <button
              type="button"
              className={`hc-tab ${activeTab === "faqs" ? "active" : ""}`}
              role="tab"
              aria-selected={activeTab === "faqs"}
              onClick={() => setActiveTab("faqs")}
            >
              FAQs <span className="hc-pill">{totalFaqs}</span>
            </button>

            <button
              type="button"
              className={`hc-tab ${activeTab === "training" ? "active" : ""}`}
              role="tab"
              aria-selected={activeTab === "training"}
              onClick={() => setActiveTab("training")}
            >
              Training Materials <span className="hc-pill">{trainingTiles.length}</span>
            </button>

            <button
              type="button"
              className={`hc-tab ${activeTab === "prompts" ? "active" : ""}`}
              role="tab"
              aria-selected={activeTab === "prompts"}
              onClick={() => setActiveTab("prompts")}
            >
              Prompt Library <span className="hc-pill">{promptSections.reduce((n, s) => n + s.items.length, 0)}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="hc-shell">
        {activeTab === "prompts" ? (
          <PromptDictionary />
        ) : activeTab === "faqs" ? (
          <div className="hc-layout" role="tabpanel" aria-label="FAQs">
            {/* Left navigation */}
            <aside className="hc-nav" aria-label="FAQ navigation">
              <div className="hc-nav-card">
                <div className="hc-search">
                  <i className="fas fa-magnifying-glass" aria-hidden="true" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search FAQs…"
                    aria-label="Search FAQs"
                  />
                  {query.trim() && (
                    <button
                      type="button"
                      className="hc-clear"
                      onClick={() => setQuery("")}
                      aria-label="Clear search"
                      title="Clear"
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>

                <div className="hc-nav-title">Sections</div>
                <div className="hc-nav-list">
                  {filteredSections.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`hc-nav-item ${activeSectionId === s.id ? "active" : ""}`}
                      onClick={() => scrollToSection(s.id)}
                    >
                      <span className="hc-nav-item-text">{s.title}</span>
                      <span className="hc-nav-item-count">{s.items.length}</span>
                    </button>
                  ))}
                </div>

                <div className="hc-nav-tip">
                  Tip: Use search to narrow results, then click a section to jump directly.
                </div>
              </div>
            </aside>

            {/* Main content */}
            <main className="hc-main">
              {filteredSections.length === 0 ? (
                <div className="hc-empty">
                  <div className="hc-empty-title">No results found</div>
                  <div className="hc-empty-sub">Try a different keyword.</div>
                </div>
              ) : (
                filteredSections.map((section) => (
                  <section
                    key={section.id}
                    id={`hc-sec-${section.id}`}
                    className="hc-section"
                    aria-label={section.title}
                  >
                    <div className="hc-section-head">
                      <div className="hc-section-title">{section.title}</div>
                      <div className="hc-section-meta">{section.items.length} articles</div>
                    </div>

                    <div className="hc-accordion">
                      {section.items.map((item, idx) => {
                        const key = `${section.id}:${idx}`;
                        const isOpen = !!openKeys[key];

                        return (
                          <div key={key} className={`hc-item ${isOpen ? "open" : ""}`}>
                            <button
                              type="button"
                              className="hc-q"
                              onClick={() => toggle(key)}
                              aria-expanded={isOpen}
                            >
                              <span className="hc-q-left">
                                <span className="hc-q-icon" aria-hidden="true">
                                  <i className="fas fa-circle-question" />
                                </span>
                                <span className="hc-q-text">{item.q}</span>
                              </span>

                              <span className="hc-chevron" aria-hidden="true">
                                <i className={`fas ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`} />
                              </span>
                            </button>

                            {isOpen && (
                              <div className="hc-a">
                                {typeof item.a === "string" ? <p>{item.a}</p> : item.a}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))
              )}
            </main>
          </div>
        ) : (
          <div className="hc-training" role="tabpanel" aria-label="Training Materials">
            <div className="hc-training-head">
              <div className="hc-training-title">Training Materials</div>
              <div className="hc-training-sub">Short videos + job aids to help you learn features quickly.</div>
            </div>

            {/* One heading + card grid per section (New first, then Archive). */}
            {trainingSections.map((section) => (
              <section key={section.id} className="tm-section" aria-label={`${section.title} training videos`}>
                <div className="tm-section-head">
                  <div className="tm-section-title">{section.title}</div>
                  <div className="tm-section-meta">{section.tiles.length} videos</div>
                </div>

                <div className="tm-grid">
                  {section.tiles.map((tile: TrainingTile) => (
                    <div key={tile.title} className="tm-card">
                      <button
                        type="button"
                        className="tm-thumb tm-thumb-btn"
                        onClick={() => openTrainingVideo(tile.videoUrl)}
                        aria-label={`Play ${tile.title} video`}
                      >
                        {(tile.thumbnailUrl || thumbMap[tile.videoUrl]) ? (
                          <img
                            className="tm-thumb-img"
                            src={tile.thumbnailUrl || thumbMap[tile.videoUrl]}
                            alt={`${tile.title} thumbnail`}
                          />
                        ) : (
                          <div className="tm-thumb-placeholder" aria-hidden="true">
                            <div className="tm-thumb-placeholder-inner">
                              <i className="fas fa-video" aria-hidden="true" />
                              <span>Training Video</span>
                            </div>
                          </div>
                        )}

                        <div className="tm-play" aria-hidden="true">
                          <i className="fas fa-play" />
                        </div>
                      </button>

                      <div className="tm-body">
                        <div className="tm-card-title">{tile.title}</div>
                        <div className="tm-desc">{tile.description}</div>

                        <div className="tm-meta">
                          <span className="tm-meta-label">Video Length:</span> {tile.videoLength}
                        </div>

                        <div className="tm-actions">
                          <button className="tm-btn" type="button" onClick={() => openTrainingVideo(tile.videoUrl)}>
                            <i className="fas fa-play" aria-hidden="true" />
                            <span>Watch</span>
                          </button>

                          {/* Download job aid (instead of just opening in a tab) */}
                          <a className="tm-btn ghost" href={toDownloadUrl(tile.jobAidUrl)}>
                            <i className="fas fa-file-arrow-down" aria-hidden="true" />
                            <span>Job Aid</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {videoModalOpen && (
              <div className="vm-backdrop" role="dialog" aria-modal="true" aria-label="Training video">
                <button className="vm-overlay" onClick={closeTrainingVideo} aria-label="Close video" />

                <div className="vm-modal">
                  <div className="vm-topbar">
                    <div className="vm-title">Training Video</div>
                    <button className="vm-close" onClick={closeTrainingVideo} aria-label="Close">
                      <i className="fas fa-times" />
                    </button>
                  </div>

                  <div className="vm-frameWrap">
                    <iframe
                      className="vm-iframe"
                      src={activeVideoUrl}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title="Vimeo video"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpCenter;
